import type { DoseStatus, DoseUnit } from '@adhd/shared';
import { useState } from 'react';
import { api, ApiError, type DoseSlot, type MedicationsToday } from '../lib/api.js';

/**
 * One scheduled dose, and the tap that records it.
 *
 * Lifted out of the medications screen so Today can offer the same row. Two
 * copies of this would drift, and the thing they would drift on is the part
 * that matters: which chip counts as already-recorded, and whether tapping it
 * again quietly rewrites a time that was right.
 */

/**
 * The three answers on every slot, in this order.
 *
 * All three are the same chip in the same colour on purpose. Colouring "missed"
 * as a fault would make the honest answer the one that costs something, and the
 * first thing that stops is the honest answering.
 *
 * `late` is a status the API can return but not one offered here — a fourth
 * button splits the taken case for no gain at the moment of tapping.
 */
/**
 * How long after logging a dose the "how did it land?" link stays on Today.
 *
 * Two hours, because that is when you would know. A stimulant taken at eight is
 * doing whatever it is going to do by ten, and the sentence worth capturing —
 * "jittery", "locked in" — arrives in that window rather than the next morning.
 *
 * After it, the card goes back to being what Today needs it to be: a name, a
 * time, three chips. The option does not disappear, it moves to the medications
 * screen where the history lives. The point is that a section competing with
 * SOS for the top of the screen should not carry a permanent control for
 * something you do occasionally.
 */
const FEELINGS_WINDOW_MS = 2 * 60 * 60 * 1000;

export const STATUS_ACTIONS: { status: DoseStatus; label: string }[] = [
  { status: 'taken', label: 'Taken' },
  { status: 'missed', label: 'Missed' },
  { status: 'skipped', label: 'Skipped' },
];

/**
 * Which of the three chips reads as already recorded.
 *
 * A dose whose status is `late` was taken. Nothing here writes that status, but
 * the API carries it, and without this the row would show three unpressed chips
 * for a dose that is already logged — so it would look untouched and get
 * answered a second time, over the top of the record it already had.
 */
export function pressedStatus(status: DoseStatus | null): DoseStatus | null {
  return status === 'late' ? 'taken' : status;
}

/**
 * Record one scheduled dose, optimistically.
 *
 * The gap between tapping and seeing the tap land is exactly where a two-second
 * habit turns into a five-second one, so the row changes immediately and only
 * reverts if the server refuses it. The server updates a slot in place rather
 * than adding a second dose, so a mistap is corrected by tapping the right
 * answer.
 */
export async function logSlotOptimistic(
  today: MedicationsToday,
  slot: DoseSlot,
  status: DoseStatus,
  setToday: (next: MedicationsToday) => void,
  setError: (message: string | null) => void,
): Promise<void> {
  // Re-sending the status a slot already holds would restamp the time it was
  // taken with the time it was tapped, quietly moving a fact that was right.
  // Normalised, so tapping the pressed chip on a dose recorded as late does not
  // rewrite it as an ordinary one.
  if (pressedStatus(slot.status) === status) return;

  const previous = today;
  setToday({
    ...today,
    slots: today.slots.map((s) =>
      s.medicationId === slot.medicationId && s.scheduledFor === slot.scheduledFor
        ? { ...s, status, takenAt: status === 'taken' ? new Date().toISOString() : null }
        : s,
    ),
  });
  setError(null);

  try {
    await api.logDose({
      medicationId: slot.medicationId,
      // Exactly as it arrived. The server matches a dose to its slot on this
      // string, so a reformatted one records a second, unscheduled dose instead
      // of filling the slot that was tapped.
      scheduledFor: slot.scheduledFor,
      // Left to the server, so every recorded time comes off one clock rather
      // than off a phone that may be minutes out.
      takenAt: null,
      status,
      note: null,
    });
  } catch (e) {
    setToday(previous);
    setError(e instanceof ApiError ? e.message : 'Could not save that dose.');
  }
}

/**
 * Change the time a dose was recorded at.
 *
 * Tapping Taken stamps the moment you tapped, which is right almost always and
 * wrong in the case that matters: you took it at eight, remembered to say so at
 * half eleven, and the record now says half eleven. Left uncorrectable, the one
 * number a prescriber might reason about — when the dose actually happened — is
 * quietly the time you happened to open your phone.
 *
 * Re-logging the same slot updates it in place rather than adding a second dose,
 * so this is the ordinary log path with an explicit timestamp.
 */
async function relogAt(
  slot: DoseSlot,
  clock: string,
  day: string,
): Promise<void> {
  // A local wall-clock time on the slot's own day, converted to a real instant
  // by the browser — which is the only party here that knows the offset.
  const [h, m] = clock.split(':').map(Number);
  const [y, mo, d] = day.split('-').map(Number);
  const when = new Date(y!, (mo ?? 1) - 1, d!, h ?? 0, m ?? 0);
  await api.logDose({
    medicationId: slot.medicationId,
    scheduledFor: slot.scheduledFor,
    takenAt: when.toISOString(),
    status: slot.status === 'late' ? 'late' : 'taken',
    note: slot.note,
  });
}

export function DoseSlotCard({
  slot,
  clockApplies,
  nowClock,
  onLog,
  compact,
  day,
  onTimeChanged,
  onExpand,
}: {
  slot: DoseSlot;
  /** Whether the device clock can be trusted to say what is still to come. */
  clockApplies: boolean;
  nowClock: string;
  onLog: (slot: DoseSlot, status: DoseStatus) => void;
  /**
   * For the side-by-side rail on Today. The card is then about a third of the
   * width it gets on the medications screen, and a name opposite a right-aligned
   * "logged 7:08 PM" is two cramped columns; the status goes under the name
   * instead, on the line that already describes the dose.
   */
  compact?: boolean;
  /** The slot's own day, for turning a wall-clock time back into an instant. */
  day: string;
  /** Called after the recorded time is changed, so the caller can refresh. */
  onTimeChanged?: () => void;
  /** Compact cards cannot hold the editor; the caller opens it full width. */
  onExpand?: () => void;
}): JSX.Element {
  const status = slotStatusText(slot, clockApplies, nowClock);

  return (
    <div className="card stack-sm stack">
      {compact ? (
        <div>
          <strong>{slot.medicationName}</strong>
          <p className="faint" style={{ marginTop: 2 }}>
            {[describeSlot(slot), status].filter((part) => part !== '').join(' · ')}
          </p>
        </div>
      ) : (
        <div className="row-between">
          <div>
            <strong>{slot.medicationName}</strong>
            <p className="faint" style={{ marginTop: 2 }}>
              {describeSlot(slot)}
            </p>
          </div>
          <span className="faint" style={{ textAlign: 'right' }}>
            {status}
          </span>
        </div>
      )}

      <div
        className="chips"
        role="group"
        aria-label={`${slot.medicationName} at ${formatClock(slot.time)}`}
      >
        {STATUS_ACTIONS.map((action) => (
          <button
            key={action.status}
            /* An assumed dose shows Taken already pressed, because that is what
               it counts as everywhere else and a screen that disagreed with the
               adherence figure would just be wrong. Drawn a shade quieter than a
               confirmed one, and tapping it writes the dose for real. */
            className={
              action.status === 'taken' && slot.assumed && slot.status === null
                ? 'chip chip-assumed'
                : 'chip'
            }
            aria-pressed={
              pressedStatus(slot.status) === action.status ||
              (action.status === 'taken' && slot.assumed && slot.status === null)
            }
            onClick={() => onLog(slot, action.status)}
          >
            {action.label}
          </button>
        ))}
      </div>

      {/* Only for a dose that was actually recorded as taken. An assumed one has
          no timestamp to correct — there is nothing to disagree with yet, and
          offering to edit it would imply the app had recorded a time it has
          not. */}
      {/*
        * Editing lives outside the card when the card is in the rail.
        *
        * A ten-chip picker and a text field do not fit in something sized to
        * 19rem for three status chips, and the rail's no-wrap rule — which
        * exists so Taken/Missed/Skipped stay on one line — pushed the whole
        * editor off the right-hand edge. In compact mode the card offers a link
        * and the caller opens a full-width panel; only the roomy card on the
        * medications screen edits in place.
        */}
      {(slot.status === 'taken' || slot.status === 'late') &&
        onTimeChanged &&
        (compact ? (
          onExpand &&
          showFeelingsPrompt(slot) && (
            <button className="btn btn-ghost btn-compact dose-time-edit" onClick={onExpand}>
              {slot.feelings && slot.feelings.length > 0
                ? `Felt ${slot.feelings.join(', ')}`
                : 'How did it land?'}
            </button>
          )
        ) : (
          <>
            <DoseFeelings slot={slot} onChanged={onTimeChanged} />
            {slot.takenAt && <TakenAtEditor slot={slot} day={day} onChanged={onTimeChanged} />}
          </>
        ))}
    </div>
  );
}


/**
 * How a dose landed — "jittery", "locked in", "flat".
 *
 * Optional and after the fact. It appears only once a dose is recorded as
 * taken, because the question does not exist before then, and it never blocks
 * the tap: logging a dose stays one tap and this sits underneath for the days
 * someone has something to say.
 *
 * Suggestions rather than a fixed list. They are the words people reach for
 * about stimulants, they are quicker than typing, and anything not on it can be
 * typed — a vocabulary that only accepts the app's words would quietly reshape
 * what gets recorded.
 */
const FEELING_SUGGESTIONS = [
  'locked in', 'jittery', 'flat', 'wired', 'calm', 'foggy',
  'no appetite', 'irritable', 'anxious', 'nothing different',
];

export function DoseFeelings({
  slot,
  onChanged,
  startOpen = false,
}: {
  slot: DoseSlot;
  onChanged: () => void;
  /**
   * True when the caller has already opened a panel for this dose. Asking
   * someone to tap "How did it land?" a second time inside a panel they opened
   * by tapping "How did it land?" is the kind of thing nobody does twice.
   */
  startOpen?: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(startOpen);
  const [picked, setPicked] = useState<string[]>(slot.feelings ?? []);
  const [custom, setCustom] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(next: string[]): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.logDose({
        medicationId: slot.medicationId,
        scheduledFor: slot.scheduledFor,
        // Unchanged. Re-logging updates the slot in place, so the recorded time
        // and status must be sent back as they are or saving a feeling would
        // quietly restamp when the dose was taken.
        takenAt: slot.takenAt,
        status: slot.status === 'late' ? 'late' : 'taken',
        note: slot.note,
        feelings: next,
      });
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    const existing = slot.feelings ?? [];
    return (
      <button className="btn btn-ghost btn-compact dose-time-edit" onClick={() => setOpen(true)}>
        {existing.length > 0 ? `Felt ${existing.join(', ')}` : 'How did it land?'}
      </button>
    );
  }

  return (
    <div className="stack-sm stack">
      {error && <div className="banner banner-caution">{error}</div>}
      <div className="chips" role="group" aria-label="How the dose landed">
        {FEELING_SUGGESTIONS.map((f) => (
          <button
            key={f}
            className="chip"
            aria-pressed={picked.includes(f)}
            onClick={() => setPicked(picked.includes(f) ? picked.filter((x) => x !== f) : [...picked, f])}
          >
            {f}
          </button>
        ))}
      </div>
      <div className="row">
        <input
          className="input"
          value={custom}
          placeholder="Something else"
          aria-label="Another word for how it landed"
          onChange={(e) => setCustom(e.target.value)}
        />
        <button
          className="btn btn-secondary btn-compact"
          disabled={busy}
          onClick={() => {
            const next = custom.trim() === '' ? picked : [...picked, custom.trim()];
            void save(next).then(() => setOpen(false));
          }}
        >
          {busy ? <span className="spinner" /> : 'Save'}
        </button>
        <button className="btn btn-ghost btn-compact" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export function TakenAtEditor({
  slot,
  day,
  onChanged,
}: {
  slot: DoseSlot;
  day: string;
  onChanged: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(() => localClockOf(slot.takenAt!));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) {
    return (
      <button className="btn btn-ghost btn-compact dose-time-edit" onClick={() => setOpen(true)}>
        Change the time
      </button>
    );
  }

  return (
    <div className="stack-sm stack">
      {error && <div className="banner banner-caution">{error}</div>}
      <div className="row">
        <input
          className="input"
          type="time"
          value={value}
          aria-label={`Time ${slot.medicationName} was taken`}
          onChange={(e) => setValue(e.target.value)}
        />
        <button
          className="btn btn-secondary btn-compact"
          disabled={busy || value === ''}
          onClick={() => {
            setBusy(true);
            setError(null);
            relogAt(slot, value, day)
              .then(() => {
                setOpen(false);
                onChanged();
              })
              .catch((e: unknown) =>
                setError(e instanceof ApiError ? e.message : 'Could not change that time.'),
              )
              .finally(() => setBusy(false));
          }}
        >
          {busy ? <span className="spinner" /> : 'Save'}
        </button>
        <button className="btn btn-ghost btn-compact" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/** `HH:MM` of an instant, in the reader's own timezone, for a time input. */
function localClockOf(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * What has been recorded against this slot, if anything worth saying.
 *
 * Only ever additive. A slot that has passed with nothing logged says nothing —
 * that is the common case in the evening, and naming it would be nagging. So is
 * a dose recorded as missed or skipped: the pressed chip already says it, and
 * repeating it in words would be the app commenting on the answer.
 */
function slotStatusText(slot: DoseSlot, clockApplies: boolean, nowClock: string): string {
  if (slot.status === 'taken' || slot.status === 'late') {
    return slot.takenAt ? `logged ${formatTime(slot.takenAt)}` : 'logged';
  }
  if (slot.status === null && clockApplies && slot.time > nowClock) return 'later';
  // Counted as taken, and saying so is the point. The word is doing real work:
  // it is the difference between what the app inferred and what the user
  // confirmed, and that difference reaches a clinician.
  if (slot.assumed && slot.status === null) return 'assumed taken';
  return '';
}

/**
 * Whether Today should still be asking how a dose landed.
 *
 * Anything already recorded stays visible for good — that is the person's own
 * words about their own dose, and hiding it after two hours would be the app
 * losing something it was told. The prompt itself is what expires.
 */
function showFeelingsPrompt(slot: DoseSlot): boolean {
  if (slot.feelings && slot.feelings.length > 0) return true;
  if (!slot.takenAt) return false;
  return Date.now() - Date.parse(slot.takenAt) < FEELINGS_WINDOW_MS;
}

/** "10 mg · 8:00" — what this particular dose is, on one line. */
export function describeSlot(slot: DoseSlot): string {
  return [doseText(slot.doseAmount, slot.doseUnit), formatClock(slot.time), slot.label]
    .filter((part): part is string => part !== null && part !== '')
    .join(' · ');
}

export function doseText(amount: number | null, unit: DoseUnit): string | null {
  if (amount === null) return null;
  // "1 tablet" rather than "1 tablets", and "10 mg" never pluralises.
  if (unit === 'tablet' || unit === 'capsule' || unit === 'patch') {
    return `${amount} ${unit}${amount === 1 ? '' : 's'}`;
  }
  return unit === 'other' ? String(amount) : `${amount} ${unit}`;
}

/**
 * "08:00" in whatever shape the reader's phone writes times.
 *
 * Printing the stored string would show 24-hour clock to someone whose device
 * has never shown them one, which reads as a different time of day entirely.
 */
export function formatClock(time: string): string {
  const parts = /^(\d{1,2}):(\d{2})$/.exec(time);
  if (!parts) return time;
  const date = new Date(2000, 0, 1, Number(parts[1]), Number(parts[2]));
  if (Number.isNaN(date.getTime())) return time;
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** `YYYY-MM-DD` in the browser's own timezone, not UTC's. */
export function localDay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Local `HH:MM`, zero-padded so it sorts against a schedule time as a string. */
export function clockNow(): string {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}
